/**
 * First Rescue (ServiceNow) Tow Pricing card — Integrations page.
 *
 * First Rescue pays on a different rate card than AA:
 *   - Separate flat fee for jumpstarts (no km math).
 *   - Tow has TWO base rates (business-hours vs after-hours, exclusive — A/H
 *     replaces business base, not additive).
 *   - Public-holiday surcharge stacks ON TOP of whichever base applies.
 *   - Extra km beyond `free_km` charged at `per_km_rate`.
 *   - Fuel Adjustment Factor (FAF) added per extra km only — first `free_km`
 *     do not accrue FAF.
 *
 * Admin only — edit form. Read-only for everyone else (matches AA card).
 * Default values reflect First Rescue's published Feb-2026 rate sheet.
 */
import { useEffect, useState } from "react";
import { Receipt, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import MoneyField from "./MoneyField";
import { TimeInput24, hhmm, hourOf } from "./DateTime24";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { fetchSnTowPricing, updateSnTowPricing } from "../lib/api";

export default function SnTowPricingCard() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSnTowPricing()
      .then(setCfg)
      .catch(() => toast.error("Failed to load First Rescue pricing"));
  }, []);

  const onSave = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // Coerce all numeric fields — Inputs return strings.
      const patch = {
        jumpstart_rate: Number(cfg.jumpstart_rate),
        jumpstart_ah_rate: Number(cfg.jumpstart_ah_rate),
        base_tow_rate: Number(cfg.base_tow_rate),
        after_hours_tow_rate: Number(cfg.after_hours_tow_rate),
        public_holiday_surcharge: Number(cfg.public_holiday_surcharge),
        free_km: Number(cfg.free_km),
        per_km_rate: Number(cfg.per_km_rate),
        faf_per_km: Number(cfg.faf_per_km),
        business_hours_start_hour: Number(cfg.business_hours_start_hour),
        business_hours_end_hour: Number(cfg.business_hours_end_hour),
      };
      const updated = await updateSnTowPricing(patch);
      setCfg(updated);
      toast.success("First Rescue pricing saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6">
        <p className="text-sm text-slate-500">Loading First Rescue pricing…</p>
      </section>
    );
  }

  return (
    <section
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-6 space-y-5"
      data-testid="sn-tow-pricing-card"
    >
      <div className="flex items-center gap-2">
        <Receipt className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
        <h2 className="font-display text-lg font-black text-slate-800 dark:text-slate-100">
          First Rescue (ServiceNow) Pricing
        </h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-3">
        Rates supplied by First Rescue (ex-GST). Separate business-hours &amp; after-hours base for both tow and jumpstart. Extra km and Fuel Adjustment Factor (FAF) apply on top of the base for all service types. Public-holiday surcharge stacks on top of whichever base applies.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <MoneyField
          label="Jumpstart · biz hours ($)"
          hint={`Includes the first ${cfg.free_km} km · extra km + FAF apply.`}
          value={cfg.jumpstart_rate ?? ""}
          onChange={(v) => setCfg({ ...cfg, jumpstart_rate: v })}
          disabled={!isAdmin}
          testid="sn-jumpstart-rate"
        />
        <MoneyField
          label="Jumpstart · after hours ($)"
          hint="Replaces the biz-hours rate outside business hours & weekends."
          value={cfg.jumpstart_ah_rate ?? ""}
          onChange={(v) => setCfg({ ...cfg, jumpstart_ah_rate: v })}
          disabled={!isAdmin}
          testid="sn-jumpstart-ah-rate"
        />
        <MoneyField
          label="Tow base · biz hours ($)"
          hint={`Includes the first ${cfg.free_km} km.`}
          value={cfg.base_tow_rate ?? ""}
          onChange={(v) => setCfg({ ...cfg, base_tow_rate: v })}
          disabled={!isAdmin}
          testid="sn-base-rate"
        />
        <MoneyField
          label="Tow base · after hours ($)"
          hint="Replaces biz base · not additive."
          value={cfg.after_hours_tow_rate ?? ""}
          onChange={(v) => setCfg({ ...cfg, after_hours_tow_rate: v })}
          disabled={!isAdmin}
          testid="sn-ah-rate"
        />
        <MoneyField
          label="Public holiday surcharge ($)"
          hint="Stacks on top of biz/after-hours base."
          value={cfg.public_holiday_surcharge ?? ""}
          onChange={(v) => setCfg({ ...cfg, public_holiday_surcharge: v })}
          disabled={!isAdmin}
          testid="sn-holiday-surcharge"
        />
        <div>
          <Label className="uppercase-label">Free km (in base)</Label>
          <Input
            type="number" step="1" min="0"
            value={cfg.free_km}
            onChange={(e) => setCfg({ ...cfg, free_km: e.target.value })}
            disabled={!isAdmin}
            data-testid="sn-free-km"
          />
          <p className="text-[11px] text-slate-400 mt-1">Round-trip kms covered by base.</p>
        </div>
        <MoneyField
          label="Per-km rate ($)"
          hint="Per km beyond the free-km block."
          value={cfg.per_km_rate ?? ""}
          onChange={(v) => setCfg({ ...cfg, per_km_rate: v })}
          disabled={!isAdmin}
          testid="sn-per-km"
        />
        <div>
          <Label className="uppercase-label">FAF per extra km ($)</Label>
          <Input
            type="number" step="0.001" min="0"
            value={cfg.faf_per_km}
            onChange={(e) => setCfg({ ...cfg, faf_per_km: e.target.value })}
            disabled={!isAdmin}
            data-testid="sn-faf"
          />
          <p className="text-[11px] text-slate-400 mt-1">Fuel Adjustment Factor · on extra km only.</p>
        </div>
        <div>
          <Label className="uppercase-label">Biz hours start (NZ)</Label>
          <TimeInput24
            value={hhmm(cfg.business_hours_start_hour)}
            onChange={(v) => setCfg({ ...cfg, business_hours_start_hour: hourOf(v) })}
            disabled={!isAdmin}
            dataTestId="sn-biz-start"
          />
        </div>
        <div>
          <Label className="uppercase-label">Biz hours end (NZ)</Label>
          <TimeInput24
            value={hhmm(cfg.business_hours_end_hour)}
            onChange={(v) => setCfg({ ...cfg, business_hours_end_hour: hourOf(v) })}
            disabled={!isAdmin}
            dataTestId="sn-biz-end"
          />
          <p className="text-[11px] text-slate-400 mt-1">Exclusive — 17:00 = after-hours from 5 pm.</p>
        </div>
      </div>

      {isAdmin && (
        <div className="flex justify-end">
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="sn-save-pricing-btn"
          >
            <Save className="h-4 w-4.5" />
            {saving ? "Saving…" : "Save First Rescue pricing"}
          </Button>
        </div>
      )}
    </section>
  );
}
